/**
 * The date the catalog, inventory and pricing APIs scope their results by,
 * tagged with the query param it is sent as.
 *
 * The APIs accept either the legacy `inventory_date` or `release_date`
 * (Frictionless Fulfillment, WEB-7500), and which one the web client sends is
 * gated by `web.feature-flag.release-date.enabled`. The param name travels
 * with the value so both are always chosen together, from the same order:
 * a release date sent as `inventory_date` (or the reverse) is still a valid
 * date, so the API would silently scope results to the wrong week.
 *
 * Build one with `getOrderDateParam` / `useOrderDateParam` from
 * `@hungryroot/rest-hooks` rather than by hand.
 */
export type OrderDateParam =
  | { release_date: string; inventory_date?: never }
  | { inventory_date: string; release_date?: never };

type OrderDateParamName = 'release_date' | 'inventory_date';

export function getOrderDateParamEntry(
  orderDate: OrderDateParam
): [OrderDateParamName, string] {
  return orderDate.release_date !== undefined
    ? ['release_date', orderDate.release_date]
    : ['inventory_date', orderDate.inventory_date];
}

/** The bare `YYYY-MM-DD` value, for keying into date-indexed response data. */
export function getOrderDateValue(orderDate: OrderDateParam): string {
  return getOrderDateParamEntry(orderDate)[1];
}

/** Sets the order date on `query` under whichever param name it carries. */
export function setOrderDateParam(
  query: URLSearchParams,
  orderDate: OrderDateParam | undefined
) {
  if (orderDate === undefined) {
    return;
  }

  const [name, value] = getOrderDateParamEntry(orderDate);
  query.set(name, value);
}

export function hasOrderDateParam(query: URLSearchParams) {
  return query.has('release_date') || query.has('inventory_date');
}
